import ReactModal from 'react-modal';
import styled from 'styled-components';

import { getPurpurColor, getPurpurSpacing, purpurMedia } from '@telialt/common-theme/src/purpurSelectors';

import { ModalSize, ModalSizeType } from './model';

const maxWidthSm = '23.75rem';
const maxWidthMd = '37.5rem';
const maxWidthLg = '60rem';
const maxWidthXl = '76.75rem';

type SizeProps = {
    $size?: ModalSizeType;
};

export const ModalOverlay = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 15001;
    overflow-x: hidden;
    overflow-y: auto;
`;

export const ModalContent = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    pointer-events: auto;
    background-color: ${getPurpurColor('functionalWhite')};
    background-clip: padding-box;
    outline: 0;
    min-height: 100%;
    margin: auto;

    ${purpurMedia('md')`
        min-height: initial;
    `};

    .close {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 15002;
    }
`;

export const ModalBody = styled.div`
    position: relative;
    flex: 1 1 auto;
`;

type ShadowVisibleProps = {
    $isVisible: boolean;
};

const ModalBodyShadow = styled.div<ShadowVisibleProps>`
    display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
    position: absolute;
    left: 0;
    width: 100%;
    height: 0.625rem;
    pointer-events: none;
    transition: opacity 0.15s;
    z-index: 15002;
`;

export const ModalBodyTopShadow = styled(ModalBodyShadow)`
    box-shadow: inset 0 0.75rem 0.5rem -0.625rem rgb(0 0 0 / 30%);
    top: 0;
`;
export const ModalBodyBottomShadow = styled(ModalBodyShadow)`
    box-shadow: inset 0 -0.75rem 0.5rem -0.625rem rgb(0 0 0 / 30%);
    bottom: 0;
`;

export const ModalBodyScrollable = styled.div`
    position: relative;
    overflow: auto;

    ${purpurMedia('md')`
        &::-webkit-scrollbar {
            width: 1.125rem;
        }

        &::-webkit-scrollbar-thumb {
            border: 6px solid transparent;
            border-radius: 0.5625rem;
            background-clip: padding-box;
            background-color: $grey;

            &:hover {
                background-color: $dark-grey;
            }

            &:window-inactive {
                background-color: $light-grey;
            }
        }
    `};
`;

type CustomHeightProps = {
    $height?: string;
};

export const ModalBodyContent = styled.div<CustomHeightProps>`
    &.with-side-spacing {
        margin-left: ${getPurpurSpacing('250')};
        margin-right: ${getPurpurSpacing('250')};

        ${purpurMedia('md')`
            margin-left: 1.875rem;
            margin-right: 1.875rem;
        `};
    }
    &.without-footer {
        margin-bottom: ${getPurpurSpacing('250')};
    }

    ${({ $height }) => !!$height && `height: ${$height}`};
    margin-top: 0;
`;

export const StyledModal = styled(ReactModal)<SizeProps>`
    width: 100%;
    margin: 0;
    z-index: 15002;
    outline: 0;
    display: flex;
    height: 100%;
    pointer-events: none;

    transform: translate(0, -3.125rem);
    transition: transform 0.3s ease-out;
    &.ReactModal__Content--after-open {
        transform: none;
    }

    ${purpurMedia('md')`
        max-width: ${maxWidthMd};
        min-height: calc(100% - 3.5rem);
        height: auto;
        margin: 1.75rem auto;
    `};

    ${({ $size }) =>
        $size === ModalSize.Sm &&
        purpurMedia('md')`
        max-width: ${maxWidthSm};
    `};
    ${({ $size }) =>
        $size === ModalSize.Lg &&
        purpurMedia('lg')`
        max-width: ${maxWidthLg};
    `};
    ${({ $size }) =>
        $size === ModalSize.Xl &&
        purpurMedia('xl')`
        max-width: ${maxWidthXl};
    `};

    &.modal-light-grey ${ModalContent} {
        background-color: ${getPurpurColor('gray50')};
    }

    &.modal-fullscreen {
        max-width: initial;
        height: auto;
        min-height: 100%;
        margin: 0;

        ${ModalContent} {
            margin: 0;
        }

        ${ModalBodyContent} {
            margin-top: 0;
        }
    }
    &.modal-mobile-not-full {
        min-height: calc(100% - 3.5rem);
        height: auto;
        margin: 1.75rem auto;
    }
`;
